import { ID, Permission, Role } from 'appwrite';
import { account, databases, databaseId, functions, storage } from '../config/appwrite';

const FUNCTION = 'editorial-tools';
const BUCKET = 'editorial-files';
const JOBS = 'editorial_jobs';

export class EditorialToolsService {
  static async call<T>(path: string, body: object = {}): Promise<T> {
    const { jwt } = await account.createJWT();
    const execution = await functions.createExecution(FUNCTION, JSON.stringify(body), false, path, 'POST', { 'X-Editorial-JWT': jwt });
    let result;
    try { result = JSON.parse(execution.responseBody); } catch { throw new Error('Der Redaktionsdienst hat keine gültige Antwort geliefert. Bitte versuche es erneut.'); }
    if (execution.responseStatusCode >= 400 || execution.status === 'failed') throw new Error(result.error || 'Die Verarbeitung ist fehlgeschlagen.');
    return result as T;
  }

  static async processIdml<T>(file: File, kind: 'articles' | 'html'): Promise<T> {
    if (!file.name.toLowerCase().endsWith('.idml')) throw new Error('Bitte wähle eine IDML-Datei aus.');
    if (file.size > 100000000) throw new Error('Die IDML-Datei darf höchstens 100 MB groß sein.');
    const user = await account.get();
    let id = ID.unique();
    let inputFileId: string | undefined, resultFileId: string | undefined, jobCreated = false, finished = false;
    try {
      const input = await storage.createFile(BUCKET, id, file, [Permission.read(Role.user(user.$id)), Permission.update(Role.user(user.$id)), Permission.delete(Role.user(user.$id))]);
      inputFileId = input.$id;
      const started = await this.call<{ jobId: string }>('/idml/start', { inputFileId, kind });
      id = started.jobId;
      jobCreated = true;
      const deadline = Date.now() + 12 * 60 * 1000;
      while (Date.now() < deadline) {
        const job = await databases.getDocument(databaseId, JOBS, id);
        if (job.resultFileId) resultFileId = job.resultFileId;
        if (job.status === 'failed') { finished = true; throw new Error(job.error || 'Die IDML-Datei konnte nicht verarbeitet werden.'); }
        if (job.status === 'completed' && job.resultFileId) {
          resultFileId = job.resultFileId;
          const download = await this.call<{ url: string; expiresAt: string }>('/idml/download', { jobId: id });
          // The short-lived link is scoped to this private file. Do not mix it with browser sessions.
          let response: Response;
          try { response = await fetch(download.url, { credentials: 'omit', referrerPolicy: 'no-referrer' }); }
          catch { throw new Error('Das Verarbeitungsergebnis konnte nicht heruntergeladen werden. Bitte versuche es erneut.'); }
          if (!response.ok) throw new Error('Das Verarbeitungsergebnis konnte nicht geladen werden. Bitte versuche es erneut.');
          const result = kind === 'html' ? await response.text() : await response.json();
          finished = true;
          return result as T;
        }
        await new Promise(resolve => window.setTimeout(resolve, 1500));
      }
      throw new Error('Die Verarbeitung dauert länger als erwartet. Bitte versuche es später erneut.');
    } finally {
      // Active jobs survive a closed browser. A scheduled function removes abandoned files after 48 hours.
      if (finished || !jobCreated) {
        await Promise.allSettled([
          ...(inputFileId ? [storage.deleteFile(BUCKET, inputFileId)] : []),
          ...(resultFileId ? [storage.deleteFile(BUCKET, resultFileId)] : []),
          ...(jobCreated ? [databases.deleteDocument(databaseId, JOBS, id)] : [])
        ]);
      }
    }
  }
}
